import { Button } from "@/components/ui/button";
import { useGetUserAssets } from "@/hooks/use-queries";
import { useFormatAmountWithCurrency } from "@/hooks/use-format-with-currency";
import { useState } from "react";

const iconMap: Record<string, string> = {
  USDT: "/icons/usdt.svg",
  USDC: "/icons/usdc.svg",
};

const networkIconMap: Record<string, string> = {
  TRC20: "/icons/trc20.png",
  ERC20: "/icons/erc20.svg",
};

const SendToUser = ({ onSelect }: { onSelect: (asset: any) => void }) => {
  const formatAmount = useFormatAmountWithCurrency();
  const { data: userAssets } = useGetUserAssets();

  const totalBalance = (userAssets ?? []).reduce(
    (sum: number, a: any) => sum + Number(a.balance),
    0,
  );

  const [selectedAssetId, setSelectedAssetId] = useState<string | null>(null);
  // Fall back to first asset if none explicitly selected
  const effectiveAssetId = selectedAssetId ?? userAssets?.[0]?.id ?? null;
  const selectedAsset = (userAssets ?? []).find(
    (a: any) => a.id === effectiveAssetId,
  );

  return (
    <div className="relative z-[999]">
      <h4 className="mt-6 text-sm font-medium">Wallet balance</h4>
      <p className="text-2xl font-medium">{formatAmount(totalBalance)}</p>

      <h4 className="mt-4 text-sm font-medium">
        Select your sub wallet to send from
      </h4>
      <div className="mt-2 flex gap-2 overflow-x-auto pb-1">
        {(userAssets ?? []).map((asset: any) => {
          const label = `${asset.token.type}-${asset.token.symbol}`;
          const icon = iconMap[asset.token.symbol] ?? "/icons/usdt.svg";
          const networkIcon = networkIconMap[asset.token.type];
          const isSelected = effectiveAssetId === asset.id;
          const [whole, decimals] = Number(asset.balance).toFixed(2).split(".");
          return (
            <button
              key={asset.id}
              type="button"
              onClick={() => setSelectedAssetId(asset.id)}
              className={`flex flex-1 flex-col gap-1 rounded-xl border-[0.7px] p-2.5 text-left transition-all ${
                isSelected
                  ? "bg-dark-primary-gradient border-transparent"
                  : "bg-dark-background-widget border-dark-stroke-3"
              }`}
            >
              <div className="flex items-center gap-1.5">
                <div className="relative size-4 shrink-0">
                  <img src={icon} alt={label} className="size-full" />
                  {networkIcon && (
                    <img
                      src={networkIcon}
                      alt={asset.token.type}
                      className="absolute -right-1 -bottom-1 size-2.5 rounded-full"
                    />
                  )}
                </div>
                <p
                  className={`text-[10px] leading-tight ${isSelected ? "text-dark-text-400" : "text-dark-text-300"}`}
                >
                  {label}
                </p>
              </div>
              <p
                className={`text-sm font-medium ${isSelected ? "text-dark-text-400" : "text-dark-text-50"}`}
              >
                {whole}
                <span className="text-[10px]">.{decimals}</span>
              </p>
            </button>
          );
        })}
      </div>

      <Button
        disabled={!selectedAsset}
        onClick={() => onSelect(selectedAsset)}
        className="text-dark-text-400 bg-dark-primary-gradient mt-6 h-11 w-full rounded-md disabled:opacity-50"
      >
        Continue
      </Button>
    </div>
  );
};

export default SendToUser;
